import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'

export const PRODUCT_MACHINERY_COMPLIANCE_QUERY_KEY =
  'productMachineryCompliance' as const

export type ComplianceActor = 'OEM' | 'SUPPLIER'

export type ProductMachineryComplianceView = {
  id: string
  completed: boolean
  completedAt: string | null
  completedByActor: ComplianceActor | null
}

export type MarkProductMachineryComplianceResult = {
  compliance: ProductMachineryComplianceView
  warnings: string[]
}

export function useGetProductMachineryCompliance(
  productId: string | undefined,
  options?: { enabled?: boolean }
) {
  return useQuery<ProductMachineryComplianceView, Error>({
    queryKey: [PRODUCT_MACHINERY_COMPLIANCE_QUERY_KEY, productId],
    queryFn: async () => {
      const response = await apiClient.get<ProductMachineryComplianceView>(
        `/v2/products/${productId}/machinery-compliance`
      )
      return response.data
    },
    enabled:
      (options?.enabled ?? true) && productId != null && productId.length > 0,
  })
}

export function useCompleteProductMachineryCompliance(productId: string) {
  const queryClient = useQueryClient()
  return useMutation<MarkProductMachineryComplianceResult, Error>({
    mutationFn: async () => {
      const response =
        await apiClient.post<MarkProductMachineryComplianceResult>(
          `/v2/products/${productId}/machinery-compliance/complete`
        )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCT_MACHINERY_COMPLIANCE_QUERY_KEY, productId],
      })
    },
  })
}

export function useUncompleteProductMachineryCompliance(productId: string) {
  const queryClient = useQueryClient()
  return useMutation<MarkProductMachineryComplianceResult, Error>({
    mutationFn: async () => {
      const response =
        await apiClient.post<MarkProductMachineryComplianceResult>(
          `/v2/products/${productId}/machinery-compliance/uncomplete`
        )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCT_MACHINERY_COMPLIANCE_QUERY_KEY, productId],
      })
    },
  })
}
